import { useEffect, useState, type MouseEvent } from 'react'
import type { FindingListItem, FindingsCell, FindingsScope } from '../../lib/api.ts'
import { formatCount } from '../../lib/findingsReport.ts'
import { totalPages } from '../../lib/pagination.ts'
import { useFindingsList } from '../../lib/useFindingsList.ts'
import { Button } from '../Button.tsx'
import { Pagination } from '../Pagination.tsx'
import { FindingsListTable } from './FindingsListTable.tsx'

/**
 * The findings behind one number of the per-check table (#556), the last
 * changed first.
 *
 * All of them, paged, rather than a handful: the number says how many there
 * are, and a list that stops short of it leaves the rest to be taken on trust.
 */
export function FindingsListModal({
  title,
  scope,
  cell,
  onOpen,
  onClose,
}: {
  /** The row and the column, as the table names them. */
  title: string
  scope: FindingsScope
  cell: FindingsCell
  onOpen: (item: FindingListItem, event: MouseEvent<HTMLAnchorElement>) => void
  onClose: () => void
}) {
  const [page, setPage] = useState(1)
  const { list, loading, error } = useFindingsList(scope, cell, page)

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-[rgba(0,9,51,0.35)] p-4"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <div className="rounded-base bg-background-raised border-stroke-weak flex max-h-[85vh] w-full max-w-[72rem] flex-col gap-3 overflow-y-auto border p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-baseline gap-2">
            <h2 className="text-tPSemi text-text-strong">{title}</h2>
            {list !== undefined && (
              <span className="text-tSmall text-text-weak tabular-nums">
                {formatCount(list.total)}, do alterado mais recentemente ao mais antigo
              </span>
            )}
          </div>
          <Button variant="tertiary" tone="neutral" size="small" onClick={onClose}>
            Fechar
          </Button>
        </div>

        {error !== undefined && <p className="text-tP text-text-error">{error}</p>}

        {list === undefined ? (
          error === undefined && <p className="text-tP text-text-weak">Carregando…</p>
        ) : list.items.length === 0 ? (
          <p className="text-tP text-text-weak">Nada aqui.</p>
        ) : (
          <div className={loading ? 'opacity-60' : ''}>
            <FindingsListTable items={list.items} onOpen={onOpen} />
          </div>
        )}

        {list !== undefined && (
          <Pagination
            currentPage={page}
            totalPages={totalPages(list.total, list.pageSize)}
            onPageChange={setPage}
          />
        )}
      </div>
    </div>
  )
}
